import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { InvestmentEntityDocumentStatusEnumType } from '__generated__/graphql';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import LoadingIndicator from 'primitives/LoadingIndicator';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import GenerateUnitStatementDialogButton from './GenerateUnitStatementDialogButton';
import InvestmentEntityDocumentSlideOver from './InvestmentEntityDocumentSlideOver';
import UploadInvestmentEntityDocuments from './UploadInvestmentEntityDocuments';

const INVESTMENT_ENTITY_QUERY = gql(`
  query InvestmentEntityDocuments($id: ID!, $statuses: [InvestmentEntityDocumentStatusEnumType!]) {
    investmentEntity(id: $id) {
      distributions {
        id
        distributedAt
        distributionDocument  # File URL
        closing {
          deal {
            schemeName
          }
        }
      }
      investmentEntityDocuments(statuses: $statuses) {
        id
        type
        name
        status
        description
        documentDate
        createdAt
        updatedAt
      }
    }
  }
`);

const InvestmentEntityDocuments: FC = () => {
  const [documentOnDisplay, setDocumentOnDisplay] = useState('');
  const { investmentEntityId } = useParams<{ investmentEntityId: string }>() as {
    investmentEntityId: string;
  };

  const { loading, error, data, refetch } = useQuery(INVESTMENT_ENTITY_QUERY, {
    variables: {
      id: investmentEntityId,
      statuses: [
        InvestmentEntityDocumentStatusEnumType.Active,
        InvestmentEntityDocumentStatusEnumType.Draft,
      ],
    },
  });

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const documents = data.investmentEntity.investmentEntityDocuments;
  const distributions = data.investmentEntity.distributions ?? [];

  return (
    <>
      <InvestmentEntityDocumentSlideOver
        open={!!documentOnDisplay}
        documentId={documentOnDisplay}
        onClose={() => setDocumentOnDisplay('')}
      />
      <div className="mb-4 flex space-x-3 items-center p-4 border rounded-md">
        <GenerateUnitStatementDialogButton investmentEntityId={investmentEntityId} />
        <UploadInvestmentEntityDocuments investmentEntityId={investmentEntityId} />
      </div>
      <DataTable
        data={documents}
        onClick={document => setDocumentOnDisplay(document.id)}
        columns={[
          {
            label: 'Name',
            fieldName: 'name',
          },
          {
            label: 'Type',
            fieldName: 'type',
          },
          {
            label: 'Status',
            fieldName: 'status',
            type: 'STATUS',
          },
          {
            label: 'Description',
            fieldName: 'description',
          },
          {
            label: 'Document Date',
            fieldName: 'documentDate',
            type: 'DATE',
          },
          {
            label: 'Created At',
            fieldName: 'createdAt',
            type: 'DATETIME',
          },
          {
            label: 'Updated At',
            fieldName: 'updatedAt',
            type: 'DATETIME',
          },
        ]}
      />
      <div className="mt-8">
        <h3 className="text-lg font-medium mb-4">Distribution Documents</h3>
        <DataTable
          data={distributions}
          columns={[
            {
              label: 'Distributed At',
              fieldName: 'distributedAt',
              type: 'DATE',
            },
            {
              label: 'Scheme Name',
              fieldName: 'closing.deal.schemeName',
            },
            {
              label: 'Document URL',
              fieldName: 'distributionDocument',
              type: 'CUSTOM',
              customValue: (url: string) =>
                url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                    onClick={e => e.stopPropagation()}
                  >
                    View Document
                  </a>
                ) : (
                  '-'
                ),
            },
          ]}
          emptyListTitle="No distribution documents"
          emptyListDescription="Distribution statements will appear here once generated"
        />
      </div>
    </>
  );
};

export default InvestmentEntityDocuments;
